import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  type TooltipProps,
} from 'recharts';
import type { MonthlySpendData } from './budgetAnalytics';

export interface MonthlySpendChartProps {
  data: MonthlySpendData;
  onSegmentClick?: (args: { categoryId: string; monthIndex: number; year: number }) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export const MonthlySpendChart: React.FC<MonthlySpendChartProps> = ({ data, onSegmentClick }) => {
  const { points, categories } = data;

  const [activeCategoryId, setActiveCategoryId] = React.useState<string | null>(null);

  const CustomTooltip = React.useCallback(
    ({ active, label, payload }: TooltipProps<number, string>) => {
      if (!active || !payload || payload.length === 0) return null;
      const item = payload[0];
      const value = typeof item.value === 'number' ? item.value : Number(item.value);

      return (
        <div
          style={{
            background: 'rgba(10, 10, 10, 0.92)',
            border: '1px solid rgba(255, 255, 255, 0.10)',
            borderRadius: 10,
            padding: '8px 10px',
            fontSize: 12,
            color: 'inherit',
          }}
        >
          <div style={{ opacity: 0.8, marginBottom: 4 }}>{label}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 9999,
                background: (item.color as string) || 'rgba(255,255,255,0.35)',
                flexShrink: 0,
              }}
            />
            <span style={{ fontWeight: 600 }}>{item.name}</span>
            <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(Number.isFinite(value) ? value : 0)}
            </span>
          </div>
        </div>
      );
    },
    []
  );

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
        <Tooltip
          shared={false}
          cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
          content={CustomTooltip}
        />
        <Legend />
        {categories.map(cat => (
          <Bar
            key={cat.id}
            dataKey={cat.id}
            name={cat.name}
            fill={cat.color}
            stackId="spend"
            opacity={!activeCategoryId || activeCategoryId === cat.id ? 1 : 0.25}
            onMouseEnter={() => setActiveCategoryId(cat.id)}
            onMouseLeave={() => setActiveCategoryId(null)}
            onClick={(evt: unknown) => {
              if (!onSegmentClick) return;
              const maybe = evt as { payload?: { monthIndex?: number } };
              const monthIndex = maybe?.payload?.monthIndex;
              if (typeof monthIndex !== 'number') return;
              onSegmentClick({ categoryId: cat.id, monthIndex, year: data.year });
            }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
